import {useReducer, useState} from 'react';

import {invoiceService} from '@/services/invoiceService';
import type {AdminBusinessInvoice} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';
import {InvoiceStatusValidator} from '@/validators/InvoiceValidator';

import type {InvoiceStatusDraft} from './types';
import updateInvoiceStatusReducer from './updateInvoiceStatusReducer';

/**
 * Recording what happened to an invoice after it was raised. The invoice's own
 * content stays frozen; only its payment state moves.
 */
const useInvoiceStatusUpdate = (
  invoice: AdminBusinessInvoice,
  onClose: VoidFunction,
  onSuccess?: () => void | Promise<void>,
) => {
  const [draft, dispatch] = useReducer(updateInvoiceStatusReducer, {
    status: invoice.meta.status,
    amountPaid: '',
    note: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (raw: InvoiceStatusDraft) => {
    setIsLoading(true);
    try {
      const payload = new InvoiceStatusValidator(
        raw,
        invoice.amount.value,
      ).value();
      await invoiceService.updateInvoiceStatus(invoice.id, payload);
      showSuccessMessage(`Invoice ${invoice.meta.reference} updated`);
      onClose();
      await onSuccess?.();
    } catch (e) {
      showErrorMessage(
        `Error updating invoice: ${getErrorMessage(e, String(e))}`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  return {draft, dispatch, handleSubmit, isLoading};
};

export default useInvoiceStatusUpdate;
